import { useSubscription, useSuspenseQuery } from '@apollo/client/react';
import { Spinner } from '@chakra-ui/react';
import { useRef } from 'react';

import { PageContentRoot } from '#components/page-content-root';
import { GET_DOCUMENT_BY_PATH } from '#graphql/queries/document';
import { DOCUMENT_UPDATED } from '#graphql/subscriptions/document-updated';
import { useAppSettings } from '#providers/app-settings-provider';
import { isValidDocumentPath } from '#utils/document-path';

import { ViewDocument } from './index';

export function DocumentByPathRouteFallback() {
  return (
    <PageContentRoot
      flex={1}
      alignSelf="stretch"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minH={0}
    >
      <Spinner size="xl" />
    </PageContentRoot>
  );
}

/**
 * Loads a document by path and renders {@link ViewDocument}.
 * Defined at module level to keep a stable tree identity and avoid re-mount loops.
 */
export function DocumentByPathRoute({
  documentPath,
}: {
  documentPath: string;
}) {
  const { documentPathRegex } = useAppSettings();
  const { data, refetch: refetchDocument } = useSuspenseQuery(
    GET_DOCUMENT_BY_PATH,
    { variables: { path: documentPath } },
  );
  const result = data?.documentByPath;

  const lastServerEditTimestampRef = useRef(0);

  useSubscription(DOCUMENT_UPDATED, {
    variables: { path: documentPath },
    skip: !isValidDocumentPath(documentPath, documentPathRegex),
    onData: ({ data: subData }) => {
      const ts = subData.data?.documentUpdated?.editTimestamp;
      if (typeof ts !== 'number') return;

      if (ts <= lastServerEditTimestampRef.current) return;

      lastServerEditTimestampRef.current = ts;
      void refetchDocument();
    },
  });

  const path =
    result?.__typename === 'Document'
      ? result.path
      : result?.__typename === 'DocumentNotFound'
        ? (result.requestedPath ?? documentPath)
        : documentPath;

  return (
    <ViewDocument
      key={path}
      document={result}
      path={path}
      refetch={refetchDocument}
      lastServerEditTimestampRef={lastServerEditTimestampRef}
    />
  );
}
